<template>
  <div class="admin-books-container fade-in">

    <!-- ── Page Header ─────────────────────────────────────── -->
    <div class="page-header mb-4">
      <div class="page-header-left">
        <h2 class="page-title">Kho Sách Hệ Thống</h2>
        <p class="page-subtitle">Quản lý và kiểm duyệt thư mục sách của thư viện</p>
      </div>
      <button @click="openAddModal" class="btn-book-primary-pill">
        <icon icon="fa-solid fa-plus" />
        <span>Thêm sách mới</span>
      </button>
    </div>

    <!-- ── Filter Toolbar ──────────────────────────────────── -->
    <div class="filter-panel mb-4">
      <!-- Top Row: Primary Search Bar & Actions -->
      <div class="filter-top-bar mb-3">
        <div class="filter-field-search">
          <label class="filter-label">
            <icon icon="fa-solid fa-magnifying-glass" class="filter-label-icon" />
            Tìm kiếm đầu sách
          </label>
          <div class="search-box-panel">
            <input
              :value="filters.search"
              type="text"
              placeholder="Tìm kiếm theo tên sách, tóm tắt..."
              class="panel-search-input"
              @input="onSearchInput($event.target.value)"
              @keyup.enter="searchNow"
            />
            <icon icon="fa-solid fa-magnifying-glass" class="panel-search-icon" />
            <button 
              v-if="filters.search" 
              class="panel-search-clear" 
              @click="clearSingleFilter('search')" 
              title="Xóa từ khóa"
            >
              <icon icon="fa-solid fa-xmark" />
            </button>
          </div>
        </div>

        <div class="filter-actions-right" v-if="activeFilterCount > 0">
          <button class="btn-clear-filters" @click="clearAllFilters" title="Đặt lại tất cả bộ lọc">
            <icon icon="fa-solid fa-rotate-left" />
            <span>Đặt lại bộ lọc</span>
            <span class="active-count-badge">{{ activeFilterCount }}</span>
          </button>
        </div>
      </div>

      <!-- Bottom Row: 5 Filter Dropdowns (Symmetrical 5-column Grid) -->
      <div class="filter-grid">
        <!-- Author -->
        <div class="filter-field filter-custom-dropdown" :class="{ 'has-active-value': filters.authorId !== '' }">
          <label class="filter-label">
            <icon icon="fa-solid fa-user-pen" class="filter-label-icon" />
            Tác giả
          </label>
          <div 
            class="custom-select-trigger" 
            :class="{ 'dropdown-active': activeDropdown === 'author', 'trigger-has-value': filters.authorId !== '' }" 
            @click.stop="toggleDropdown('author')"
          >
            <span class="trigger-text">{{ currentAuthorLabel }}</span>
            <span v-if="filters.authorId !== ''" class="trigger-clear-btn" @click.stop="clearSingleFilter('authorId')" title="Bỏ lọc tác giả">
              <icon icon="fa-solid fa-circle-xmark" />
            </span>
            <icon v-else icon="fa-solid fa-chevron-down" class="trigger-arrow" />
          </div>
          <div v-if="activeDropdown === 'author'" class="custom-select-dropdown" @click.stop>
            <div class="dropdown-search-wrapper">
              <input 
                type="text" 
                v-model="searchQueryAuthor" 
                class="dropdown-search-input" 
                placeholder="Tìm tác giả..." 
                ref="searchAuthorInput"
              />
              <icon icon="fa-solid fa-magnifying-glass" class="dropdown-search-icon" />
            </div>
            <div class="dropdown-options-list">
              <div 
                class="dropdown-option-item" 
                :class="{ 'option-selected': filters.authorId === '' }" 
                @click="selectAuthor('')"
              >
                <span>Tất cả tác giả</span>
                <icon v-if="filters.authorId === ''" icon="fa-solid fa-check" class="check-icon" />
              </div>
              <div 
                v-for="auth in filteredDropdownAuthors" 
                :key="auth.id" 
                class="dropdown-option-item"
                :class="{ 'option-selected': filters.authorId === auth.id }"
                @click="selectAuthor(auth.id)"
              >
                <span>{{ auth.name }}<template v-if="auth.yob || auth.yod"> ({{ auth.yob || '?' }} - {{ auth.yod || '?' }})</template></span>
                <icon v-if="filters.authorId === auth.id" icon="fa-solid fa-check" class="check-icon" />
              </div>
              <div v-if="filteredDropdownAuthors.length === 0" class="dropdown-empty">
                Không tìm thấy tác giả
              </div>
            </div>
          </div>
        </div>

        <!-- Translator -->
        <div class="filter-field filter-custom-dropdown" :class="{ 'has-active-value': filters.translatorId !== '' }">
          <label class="filter-label">
            <icon icon="fa-solid fa-language" class="filter-label-icon" />
            Dịch giả
          </label>
          <div 
            class="custom-select-trigger" 
            :class="{ 'dropdown-active': activeDropdown === 'translator', 'trigger-has-value': filters.translatorId !== '' }" 
            @click.stop="toggleDropdown('translator')"
          >
            <span class="trigger-text">{{ currentTranslatorLabel }}</span>
            <span v-if="filters.translatorId !== ''" class="trigger-clear-btn" @click.stop="clearSingleFilter('translatorId')" title="Bỏ lọc dịch giả">
              <icon icon="fa-solid fa-circle-xmark" />
            </span>
            <icon v-else icon="fa-solid fa-chevron-down" class="trigger-arrow" />
          </div>
          <div v-if="activeDropdown === 'translator'" class="custom-select-dropdown" @click.stop>
            <div class="dropdown-search-wrapper">
              <input 
                type="text" 
                v-model="searchQueryTranslator" 
                class="dropdown-search-input" 
                placeholder="Tìm dịch giả..." 
                ref="searchTranslatorInput"
              />
              <icon icon="fa-solid fa-magnifying-glass" class="dropdown-search-icon" />
            </div>
            <div class="dropdown-options-list">
              <div 
                class="dropdown-option-item" 
                :class="{ 'option-selected': filters.translatorId === '' }" 
                @click="selectTranslator('')"
              >
                <span>Tất cả dịch giả</span>
                <icon v-if="filters.translatorId === ''" icon="fa-solid fa-check" class="check-icon" />
              </div>
              <div 
                v-for="t in filteredDropdownTranslators" 
                :key="t.id" 
                class="dropdown-option-item"
                :class="{ 'option-selected': filters.translatorId === t.id }"
                @click="selectTranslator(t.id)"
              >
                <span>{{ t.name }}</span>
                <icon v-if="filters.translatorId === t.id" icon="fa-solid fa-check" class="check-icon" />
              </div>
              <div v-if="filteredDropdownTranslators.length === 0" class="dropdown-empty">
                Không tìm thấy dịch giả
              </div>
            </div>
          </div>
        </div>

        <!-- Category -->
        <div class="filter-field filter-custom-dropdown" :class="{ 'has-active-value': filters.categoryId !== '' }">
          <label class="filter-label">
            <icon icon="fa-solid fa-tags" class="filter-label-icon" />
            Thể loại
          </label>
          <div 
            class="custom-select-trigger" 
            :class="{ 'dropdown-active': activeDropdown === 'category', 'trigger-has-value': filters.categoryId !== '' }" 
            @click.stop="toggleDropdown('category')"
          >
            <span class="trigger-text">{{ currentCategoryLabel }}</span>
            <span v-if="filters.categoryId !== ''" class="trigger-clear-btn" @click.stop="clearSingleFilter('categoryId')" title="Bỏ lọc thể loại">
              <icon icon="fa-solid fa-circle-xmark" />
            </span>
            <icon v-else icon="fa-solid fa-chevron-down" class="trigger-arrow" />
          </div>
          <div v-if="activeDropdown === 'category'" class="custom-select-dropdown" @click.stop>
            <div class="dropdown-search-wrapper">
              <input 
                type="text" 
                v-model="searchQueryCategory" 
                class="dropdown-search-input" 
                placeholder="Tìm thể loại..." 
                ref="searchCategoryInput"
              />
              <icon icon="fa-solid fa-magnifying-glass" class="dropdown-search-icon" />
            </div>
            <div class="dropdown-options-list">
              <div 
                class="dropdown-option-item" 
                :class="{ 'option-selected': filters.categoryId === '' }" 
                @click="selectCategory('')"
              >
                <span>Tất cả thể loại</span>
                <icon v-if="filters.categoryId === ''" icon="fa-solid fa-check" class="check-icon" />
              </div>
              <div 
                v-for="c in filteredDropdownCategories" 
                :key="c.id" 
                class="dropdown-option-item"
                :class="{ 'option-selected': filters.categoryId === c.id }"
                @click="selectCategory(c.id)"
              >
                <span>{{ c.name }}</span>
                <icon v-if="filters.categoryId === c.id" icon="fa-solid fa-check" class="check-icon" />
              </div>
              <div v-if="filteredDropdownCategories.length === 0" class="dropdown-empty">
                Không tìm thấy thể loại
              </div>
            </div>
          </div>
        </div>

        <!-- Status -->
        <div class="filter-field filter-custom-dropdown" :class="{ 'has-active-value': filters.status !== 'ALL' }">
          <label class="filter-label">
            <icon icon="fa-solid fa-circle-half-stroke" class="filter-label-icon" />
            Trạng thái
          </label>
          <div 
            class="custom-select-trigger" 
            :class="{ 'dropdown-active': activeDropdown === 'status', 'trigger-has-value': filters.status !== 'ALL' }" 
            @click.stop="toggleDropdown('status')"
          >
            <span class="trigger-text">{{ currentStatusLabel }}</span>
            <span v-if="filters.status !== 'ALL'" class="trigger-clear-btn" @click.stop="clearSingleFilter('status')" title="Bỏ lọc trạng thái">
              <icon icon="fa-solid fa-circle-xmark" />
            </span>
            <icon v-else icon="fa-solid fa-chevron-down" class="trigger-arrow" />
          </div>
          <div v-if="activeDropdown === 'status'" class="custom-select-dropdown" @click.stop>
            <div class="dropdown-search-wrapper">
              <input 
                type="text" 
                v-model="searchQueryStatus" 
                class="dropdown-search-input" 
                placeholder="Tìm trạng thái..." 
                ref="searchStatusInput"
              />
              <icon icon="fa-solid fa-magnifying-glass" class="dropdown-search-icon" />
            </div>
            <div class="dropdown-options-list">
              <div 
                v-for="opt in filteredDropdownStatuses" 
                :key="opt.value" 
                class="dropdown-option-item"
                :class="{ 'option-selected': filters.status === opt.value }"
                @click="selectStatus(opt.value)"
              >
                <span>{{ opt.label }}</span>
                <icon v-if="filters.status === opt.value" icon="fa-solid fa-check" class="check-icon" />
              </div>
              <div v-if="filteredDropdownStatuses.length === 0" class="dropdown-empty">
                Không tìm thấy trạng thái
              </div>
            </div>
          </div>
        </div>

        <!-- Cover (Ảnh bìa) -->
        <div class="filter-field filter-field-cover filter-custom-dropdown" :class="{ 'has-active-value': filters.hasCover !== 'ALL' }">
          <label class="filter-label">
            <icon icon="fa-solid fa-image" class="filter-label-icon" />
            Ảnh bìa
          </label>
          <div 
            class="custom-select-trigger" 
            :class="{ 'dropdown-active': activeDropdown === 'cover', 'trigger-has-value': filters.hasCover !== 'ALL' }" 
            @click.stop="toggleDropdown('cover')"
          >
            <span class="trigger-text">{{ currentCoverLabel }}</span>
            <span v-if="filters.hasCover !== 'ALL'" class="trigger-clear-btn" @click.stop="clearSingleFilter('hasCover')" title="Bỏ lọc ảnh bìa">
              <icon icon="fa-solid fa-circle-xmark" />
            </span>
            <icon v-else icon="fa-solid fa-chevron-down" class="trigger-arrow" />
          </div>
          <div v-if="activeDropdown === 'cover'" class="custom-select-dropdown" @click.stop>
            <div class="dropdown-options-list mt-1">
              <div 
                v-for="opt in coverOptions" 
                :key="opt.value" 
                class="dropdown-option-item"
                :class="{ 'option-selected': filters.hasCover === opt.value }"
                @click="selectCover(opt.value)"
              >
                <span>{{ opt.label }}</span>
                <icon v-if="filters.hasCover === opt.value" icon="fa-solid fa-check" class="check-icon" />
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Active Filter Chips Bar -->
      <div v-if="activeFilterTags.length > 0" class="active-filter-chips-row">
        <span class="chips-label">Đang lọc:</span>
        <div class="chips-list">
          <span 
            v-for="tag in activeFilterTags" 
            :key="tag.key" 
            class="filter-chip"
            @click="clearSingleFilter(tag.key)"
            title="Nhấp để xóa lọc"
          >
            <span>{{ tag.label }}</span>
            <icon icon="fa-solid fa-xmark" class="chip-remove-icon" />
          </span>
        </div>
      </div>
    </div>

    <!-- ── Books Table Panel ────────────────────────────────── -->
    <div class="dashboard-panel">
      <div class="panel-header">
        <div class="panel-title-group">
          <h3 class="panel-title">Danh sách sách</h3>
          <span class="panel-subtitle">Tìm thấy {{ filteredBooks.length }} tác phẩm</span>
        </div>
      </div>

      <!-- ── Pagination (Top) ────────────────────────────────── -->
      <div v-if="totalPages > 1" class="pagination-wrapper pagination-top">
        <div class="pagination-info">
          Hiển thị <strong>{{ currentPage * pageSize + 1 }}</strong> -
          <strong>{{ Math.min((currentPage + 1) * pageSize, totalElements) }}</strong>
          trong số <strong>{{ totalElements }}</strong> sách
        </div>
        <div class="pagination-controls">
          <button
            @click="changePage(currentPage - 1)"
            :disabled="currentPage === 0"
            class="btn-pagination"
            title="Trang trước"
          >
            <icon icon="fa-solid fa-chevron-left" />
          </button>
          <button
            v-for="p in visiblePages"
            :key="p"
            @click="changePage(p)"
            class="btn-pagination"
            :class="{ active: currentPage === p }"
          >
            {{ p + 1 }}
          </button>
          <button
            @click="changePage(currentPage + 1)"
            :disabled="currentPage === totalPages - 1"
            class="btn-pagination"
            title="Trang sau"
          >
            <icon icon="fa-solid fa-chevron-right" />
          </button>
        </div>
      </div>

      <div class="table-responsive-wrapper">
        <table class="custom-admin-table">
          <thead>
            <tr>
              <th class="col-cover text-center">Ảnh Bìa</th>
              <th class="col-bookinfo">Thông Tin Sách</th>
              <th class="col-relations">Tác giả / Thể loại</th>
              <th class="text-center col-rating">Đánh Giá</th>
              <th class="text-center col-status">
                Trạng Thái
                <icon 
                  icon="fa-solid fa-circle-question" 
                  class="help-tooltip-icon" 
                  data-tooltip-pos="bottom" 
                  data-tooltip="Ý nghĩa màu sắc:
• Trắng: Đang phát hành
• Vàng: Đang chờ xóa
• Xanh lam: Đang chờ khôi phục
• Đỏ: Đã xóa" 
                />
              </th>
              <th class="text-center col-actions">Thao Tác</th>
            </tr>
          </thead>
          <tbody>
            <!-- Skeleton Loader -->
            <template v-if="isLoading">
              <tr v-for="n in 5" :key="'skeleton-' + n" class="skeleton-row">
                <!-- Cover -->
                <td class="text-center">
                  <div class="book-cover-wrapper">
                    <span class="skeleton-bar" style="width: 48px; height: 72px; border-radius: 4px;"></span>
                  </div>
                </td>
                <!-- Book Info -->
                <td>
                  <div class="book-info-cell">
                    <span class="skeleton-bar" style="width: 150px; height: 16px; margin-bottom: 8px;"></span>
                    <span class="skeleton-bar" style="width: 90px; height: 12px;"></span>
                  </div>
                </td>
                <!-- Relations -->
                <td>
                  <div class="relations-cell">
                    <div class="relation-row mb-2">
                      <span class="skeleton-bar" style="width: 120px; height: 16px;"></span>
                    </div>
                    <div class="relation-row">
                      <span class="skeleton-bar" style="width: 140px; height: 16px;"></span>
                    </div>
                  </div>
                </td>
                <!-- Rating -->
                <td class="text-center">
                  <span class="skeleton-bar" style="width: 42px; height: 20px; border-radius: 10px;"></span>
                </td>
                <!-- Status -->
                <td class="text-center">
                  <span class="skeleton-bar skeleton-badge"></span>
                </td>
                <!-- Actions -->
                <td class="text-center">
                  <div class="skeleton-actions">
                    <span class="skeleton-btn"></span>
                    <span class="skeleton-btn"></span>
                    <span class="skeleton-btn"></span>
                  </div>
                </td>
              </tr>
            </template>

            <!-- Data Rows -->
            <template v-else>
              <tr v-for="book in filteredBooks" :key="book.id" class="table-row-hover">
                <!-- Cover -->
                <td class="text-center">
                  <div class="book-cover-wrapper">
                    <img v-if="book.cover" :src="book.cover.startsWith('data:') || book.cover.startsWith('http') ? book.cover : 'data:image/png;base64,' + book.cover" class="book-cover-img" :alt="book.name" loading="lazy" />
                    <div v-else class="book-cover-placeholder">
                      <icon icon="fa-solid fa-book-open" />
                    </div>
                  </div>
                </td>

                <!-- Book Info -->
                <td>
                  <div class="book-info-cell">
                    <span class="book-name">{{ book.name }}</span>
                    <a v-if="book.link_shopee" :href="book.link_shopee" target="_blank" rel="noopener noreferrer" class="shopee-link">
                      <icon icon="fa-solid fa-cart-shopping" />
                      Shopee
                    </a>
                  </div>
                </td>

                <!-- Relations -->
                <td>
                  <div class="relations-cell">
                    <div v-if="book.authors && book.authors.length" class="relation-row">
                      <span class="relation-label">Tác giả</span>
                      <div class="badge-group">
                        <span
                          v-for="a in book.authors"
                          :key="a.id"
                          class="badge-author"
                          :class="{ 'badge-deleted': a.status !== 0 && a.status != null }"
                          :data-tooltip="a.status !== 0 && a.status != null ? 'Tác giả đã bị xóa' : undefined"
                        >{{ a.name }}<template v-if="a.yob || a.yod"> ({{ a.yob || '?' }} - {{ a.yod || '?' }})</template></span>
                      </div>
                    </div>
                    <div v-if="book.translators && book.translators.length" class="relation-row">
                      <span class="relation-label">Dịch giả</span>
                      <div class="badge-group">
                        <span
                          v-for="t in book.translators"
                          :key="t.id"
                          class="badge-translator"
                          :class="{ 'badge-deleted': t.status !== 0 && t.status != null }"
                          :data-tooltip="t.status !== 0 && t.status != null ? 'Dịch giả đã bị xóa' : undefined"
                        >{{ t.name }}</span>
                      </div>
                    </div>
                    <div v-if="book.categories && book.categories.length" class="relation-row">
                      <span class="relation-label">Thể loại</span>
                      <div class="badge-group">
                        <span
                          v-for="c in book.categories"
                          :key="c.id"
                          class="badge-category"
                          :class="{ 'badge-deleted': c.status !== 0 && c.status != null }"
                          :data-tooltip="c.status !== 0 && c.status != null ? 'Thể loại đã bị xóa' : undefined"
                        >{{ c.name }}</span>
                      </div>
                    </div>
                  </div>
                </td>

                <!-- Rating -->
                <td class="text-center">
                  <div class="rating-chip">
                    <icon icon="fa-solid fa-star" class="rating-star" />
                    <span>{{ book.evaluate ? book.evaluate.toFixed(1) : '0.0' }}</span>
                  </div>
                </td>

                <!-- Status -->
                <td class="text-center">
                  <span
                    class="status-badge-custom"
                    :class="getStatusBadgeClass(book)"
                    :data-tooltip="getStatusTooltip(book)"
                  >
                    {{ getStatusLabel(book) }}
                  </span>
                </td>

                <!-- Actions -->
                <td class="text-center">
                  <div class="action-btn-group">
                    <button @click="openViewModal(book)" class="btn-action-icon btn-action-view" data-tooltip="Xem chi tiết">
                      <icon icon="fa-solid fa-eye" />
                    </button>
                    <template v-if="book.status !== 2">
                      <button @click="openEditModal(book)" class="btn-action-icon btn-action-edit" data-tooltip="Chỉnh sửa">
                        <icon icon="fa-solid fa-pen-to-square" />
                      </button>
                    </template>
                    <button @click="openHistoryModal(book)" class="btn-action-icon btn-action-history" data-tooltip="Xem lịch sử">
                      <icon icon="fa-solid fa-clock-rotate-left" />
                    </button>
                    <template v-if="book.status !== 2">
                      <button @click="confirmDelete(book)" class="btn-action-icon btn-action-delete" data-tooltip="Xóa bỏ">
                        <icon icon="fa-solid fa-trash-can" />
                      </button>
                    </template>
                    <template v-else>
                      <button @click="confirmRestore(book)" class="btn-action-icon btn-action-restore" :data-tooltip="isAdmin ? 'Khôi phục' : 'Yêu cầu khôi phục'">
                        <icon icon="fa-solid fa-trash-can-arrow-up" />
                      </button>
                    </template>
                  </div>
                </td>
              </tr>

              <tr v-if="filteredBooks.length === 0">
                <td colspan="6" class="empty-state-row">
                  <icon icon="fa-solid fa-inbox" class="empty-icon" />
                  <span>Không tìm thấy cuốn sách nào phù hợp bộ lọc.</span>
                </td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>

      <!-- ── Pagination ──────────────────────────────────────── -->
      <div v-if="totalPages > 1" class="pagination-wrapper">
        <div class="pagination-info">
          Hiển thị <strong>{{ currentPage * pageSize + 1 }}</strong> -
          <strong>{{ Math.min((currentPage + 1) * pageSize, totalElements) }}</strong>
          trong số <strong>{{ totalElements }}</strong> sách
        </div>
        <div class="pagination-controls">
          <button
            @click="changePage(currentPage - 1)"
            :disabled="currentPage === 0"
            class="btn-pagination"
            title="Trang trước"
          >
            <icon icon="fa-solid fa-chevron-left" />
          </button>
          <button
            v-for="p in visiblePages"
            :key="p"
            @click="changePage(p)"
            class="btn-pagination"
            :class="{ active: currentPage === p }"
          >
            {{ p + 1 }}
          </button>
          <button
            @click="changePage(currentPage + 1)"
            :disabled="currentPage === totalPages - 1"
            class="btn-pagination"
            title="Trang sau"
          >
            <icon icon="fa-solid fa-chevron-right" />
          </button>
        </div>
      </div>

    </div>

    <!-- ── Add / Edit Modal ────────────────────────────────── -->
    <teleport to="body">
      <div v-if="modalVisible" class="modal-backdrop-custom">
        <div class="modal-card-custom modal-card-lg">
          <div class="modal-header-custom">
            <h3 class="modal-title-custom">
              <icon :icon="isEditMode ? 'fa-solid fa-pen-to-square' : 'fa-solid fa-plus'" />
              {{ isEditMode ? 'Cập nhật thông tin sách' : 'Thêm sách mới vào kho' }}
            </h3>
            <button @click="closeModal" class="btn-close-modal" aria-label="Đóng">
              <icon icon="fa-solid fa-xmark" />
            </button>
          </div>

          <div class="modal-body-custom">
            <div class="book-modal-grid">

              <!-- Left: Cover -->
              <div class="book-modal-left">
                <div 
                  class="cover-preview-box clickable-preview" 
                  @click="triggerFileInput"
                  @dragover.prevent="dragOver = true"
                  @dragleave.prevent="dragOver = false"
                  @drop.prevent="onFileDrop"
                  :class="{ 'drag-over': dragOver }"
                  title="Nhấp hoặc kéo thả ảnh bìa vào đây"
                >
                  <img v-if="form.cover" :src="form.cover.startsWith('data:') || form.cover.startsWith('http') ? form.cover : 'data:image/png;base64,' + form.cover" class="cover-preview-img" alt="Ảnh bìa" />
                  <div v-else class="cover-preview-placeholder">
                    <icon icon="fa-solid fa-cloud-arrow-up" />
                    <span>Chọn ảnh hoặc kéo thả vào đây</span>
                  </div>
                  <input type="file" accept="image/*" style="display: none" ref="fileInput" @change="onFileChange" />
                </div>
                <div class="form-field mt-3 text-center">
                  <span class="text-muted fs-8">Kích thước tối đa 2MB (Định dạng ảnh)</span>
                </div>
              </div>

              <!-- Right: Detail Fields -->
              <div class="book-modal-right">
                <div class="form-field mb-3">
                  <label class="form-field-label">Tên sách <span class="required-mark">*</span></label>
                  <input v-model="form.name" type="text" class="custom-input-modal" :class="{ 'input-error': errors.name }" placeholder="Nhập tên đầu sách..." ref="nameInput" />
                  <span v-if="errors.name" class="field-error-msg">{{ errors.name }}</span>
                </div>

                <div class="form-field mb-3">
                  <label class="form-field-label">Tóm tắt nội dung <span class="required-mark">*</span></label>
                  <textarea v-model="form.content" class="custom-textarea-modal" :class="{ 'input-error': errors.content }" placeholder="Viết tóm tắt ngắn (tối thiểu 10 ký tự)..." rows="3"></textarea>
                  <span v-if="errors.content" class="field-error-msg">{{ errors.content }}</span>
                </div>

                <div class="form-field mb-4">
                  <label class="form-field-label">Link Shopee Affiliate</label>
                  <input v-model="form.link_shopee" type="text" class="custom-input-modal" placeholder="https://shopee.vn/..." />
                </div>

                <!-- Relations -->
                <div class="relations-section">
                  <h4 class="relations-section-title">Gán mối quan hệ</h4>
                  <div class="relations-grid">
                    <!-- Authors -->
                    <div class="relation-card relation-card-full" :class="{ 'card-error': errors.authors }">
                      <div class="d-flex justify-content-between align-items-center mb-2">
                        <span class="relation-card-label m-0">
                          <icon icon="fa-solid fa-user-pen" class="me-1" />Tác giả <span class="required-mark">*</span>
                        </span>
                      </div>
                      
                      <!-- Selected Chips -->
                      <div v-if="form.authorIds.length > 0" class="selected-relations-chips mb-2">
                        <span v-for="id in form.authorIds" :key="id" class="relation-chip" @click="deselectAuthor(id)" title="Nhấp để bỏ chọn">
                          {{ loadedAuthors[id] || loadedAuthorNames[id] || id }}
                          <icon icon="fa-solid fa-xmark" class="ms-1 chip-remove-icon" />
                        </span>
                      </div>

                      <div class="relation-search-wrapper mb-2" style="max-width: 280px;">
                        <input type="text" v-model="searchAuthor" class="form-control form-control-sm relation-search-input" placeholder="Tìm tác giả..." />
                      </div>
                      <div class="checklist-box checklist-grid">
                        <label v-for="auth in sortedAuthors" :key="auth.id" class="checklist-item">
                          <input type="checkbox" :value="auth.id" v-model="form.authorIds" class="check-input" />
                          <span>{{ auth.name }}<template v-if="auth.yob || auth.yod"> ({{ auth.yob || '?' }} - {{ auth.yod || '?' }})</template></span>
                        </label>
                        <span v-if="!sortedAuthors.length" class="checklist-empty">Không còn tác giả nào để chọn</span>
                      </div>
                      <span v-if="errors.authors" class="field-error-msg mt-1 d-block">{{ errors.authors }}</span>
                    </div>

                    <!-- Translators -->
                    <div class="relation-card relation-card-full">
                      <div class="d-flex justify-content-between align-items-center mb-2">
                        <span class="relation-card-label m-0">
                          <icon icon="fa-solid fa-language" class="me-1" />Dịch giả
                        </span>
                      </div>

                      <!-- Selected Chips -->
                      <div v-if="form.translatorIds.length > 0" class="selected-relations-chips mb-2">
                        <span v-for="id in form.translatorIds" :key="id" class="relation-chip" @click="deselectTranslator(id)" title="Nhấp để bỏ chọn">
                          {{ loadedTranslators[id] || id }}
                          <icon icon="fa-solid fa-xmark" class="ms-1 chip-remove-icon" />
                        </span>
                      </div>

                      <div class="relation-search-wrapper mb-2" style="max-width: 280px;">
                        <input type="text" v-model="searchTranslator" class="form-control form-control-sm relation-search-input" placeholder="Tìm dịch giả..." />
                      </div>
                      <div class="checklist-box checklist-grid">
                        <label v-for="t in sortedTranslators" :key="t.id" class="checklist-item">
                          <input type="checkbox" :value="t.id" v-model="form.translatorIds" class="check-input" />
                          <span>{{ t.name }}</span>
                        </label>
                        <span v-if="!sortedTranslators.length" class="checklist-empty">Không còn dịch giả nào để chọn</span>
                      </div>
                    </div>

                    <!-- Categories — full width -->
                    <div class="relation-card relation-card-full" :class="{ 'card-error': errors.categories }">
                      <div class="d-flex justify-content-between align-items-center mb-2">
                        <span class="relation-card-label m-0">
                          <icon icon="fa-solid fa-tags" class="me-1" />Thể loại <span class="required-mark">*</span>
                        </span>
                      </div>

                      <!-- Selected Chips -->
                      <div v-if="form.categoryIds.length > 0" class="selected-relations-chips mb-2">
                        <span v-for="id in form.categoryIds" :key="id" class="relation-chip" @click="deselectCategory(id)" title="Nhấp để bỏ chọn">
                          {{ loadedCategories[id] || id }}
                          <icon icon="fa-solid fa-xmark" class="ms-1 chip-remove-icon" />
                        </span>
                      </div>

                      <div class="relation-search-wrapper mb-2" style="max-width: 280px;">
                        <input type="text" v-model="searchCategory" class="form-control form-control-sm relation-search-input" placeholder="Tìm thể loại..." />
                      </div>
                      <div class="checklist-box checklist-grid">
                        <label v-for="c in sortedCategories" :key="c.id" class="checklist-item">
                          <input type="checkbox" :value="c.id" v-model="form.categoryIds" class="check-input" />
                          <span>{{ c.name }}</span>
                        </label>
                        <span v-if="!sortedCategories.length" class="checklist-empty">Không còn thể loại nào để chọn</span>
                      </div>
                      <span v-if="errors.categories" class="field-error-msg mt-1 d-block">{{ errors.categories }}</span>
                    </div>
                  </div>
                </div>
              </div>

            </div>
          </div>

          <div class="modal-footer-custom">
            <button @click="closeModal" class="btn-modal-cancel" :disabled="isLoading">Hủy bỏ</button>
            <button @click="saveBook" class="btn-modal-confirm" :disabled="isLoading || !form.name.trim() || isUnchanged">
              <icon v-if="isLoading" icon="fa-solid fa-spinner" class="fa-spin me-1" />
              Lưu dữ liệu
            </button>
          </div>
        </div>
      </div>
    </teleport>

    <!-- ── Delete Confirmation Modal ──────────────────────── -->
    <teleport to="body">
      <div v-if="deleteModalVisible" class="modal-backdrop-custom">
        <div class="modal-card-custom modal-card-sm">
          <div class="modal-sheet-handle"></div>
          <div class="modal-header-custom modal-header-danger">
            <h3 class="modal-title-custom modal-title-danger">
              <icon icon="fa-solid fa-triangle-exclamation" />
              {{ deleteModalTitle }}
            </h3>
            <button @click="closeDeleteModal" class="btn-close-modal" aria-label="Đóng">
              <icon icon="fa-solid fa-xmark" />
            </button>
          </div>
          <div class="modal-body-custom">
            <p class="delete-confirm-text" v-html="deleteConfirmText"></p>
            <p class="delete-warn-text mb-3">
              <icon icon="fa-solid fa-circle-info" />
              {{ deleteWarningText }}
            </p>
            <div class="form-field">
              <label class="form-field-label">Lý do xóa <span class="required-mark">*</span></label>
              <textarea
                v-model="deleteReason"
                class="custom-textarea-modal"
                :class="{ 'input-error': errors.deleteReason }"
                placeholder="Nhập tối thiểu 10 ký tự. Ví dụ: Sách này lỗi thời hoặc bị trùng lặp..."
                ref="deleteReasonInput"
              ></textarea>
              <div class="d-flex justify-content-between align-items-center mt-1">
                <span v-if="errors.deleteReason" class="field-error-msg m-0">{{ errors.deleteReason }}</span>
                <span v-else></span>
                <span class="fs-8-5 text-muted">
                  {{ deleteReason ? deleteReason.length : 0 }}/500 ký tự (tối thiểu 10)
                </span>
              </div>
            </div>
          </div>
          <div class="modal-footer-custom">
            <button @click="closeDeleteModal" class="btn-modal-cancel" :disabled="isLoading">Hủy bỏ</button>
            <button @click="deleteBook" class="btn-modal-danger" :disabled="isLoading || !deleteReason || deleteReason.trim().length < 10 || deleteReason.trim().length > 500">
              <icon v-if="isLoading" icon="fa-solid fa-spinner" class="fa-spin me-1" />
              {{ deleteButtonText }}
            </button>
          </div>
        </div>
      </div>
    </teleport>

    <!-- ── Restore Modal ────────────────────────────────────── -->
    <teleport to="body">
      <div v-if="restoreModalVisible" class="modal-backdrop-custom">
        <div class="modal-card-custom modal-card-sm">
          <div class="modal-sheet-handle"></div>
          <div class="modal-header-custom modal-header-restore">
            <h3 class="modal-title-custom modal-title-restore">
              <icon icon="fa-solid fa-trash-can-arrow-up" />
              {{ restoreModalTitle }}
            </h3>
            <button @click="closeRestoreModal" class="btn-close-modal" aria-label="Đóng">
              <icon icon="fa-solid fa-xmark" />
            </button>
          </div>
          <div class="modal-body-custom">
            <p class="delete-confirm-text" v-html="restoreConfirmText"></p>
            <p class="restore-info-text mb-3">
              <icon icon="fa-solid fa-circle-info" />
              {{ restoreWarningText }}
            </p>
            <div class="form-field">
              <label class="form-field-label">
                {{ isAdmin ? 'Lý do khôi phục' : 'Lý do yêu cầu khôi phục' }} <span class="required-mark">*</span>
              </label>
              <textarea
                v-model="restoreReason"
                class="custom-textarea-modal"
                :class="{ 'input-error': errors.restoreReason }"
                :placeholder="isAdmin ? 'Nhập tối thiểu 10 ký tự lý do khôi phục...' : 'Nhập tối thiểu 10 ký tự lý do yêu cầu khôi phục...'"
                ref="restoreReasonInput"
              ></textarea>
              <div class="d-flex justify-content-between align-items-center mt-1">
                <span v-if="errors.restoreReason" class="field-error-msg m-0">{{ errors.restoreReason }}</span>
                <span v-else></span>
                <span class="fs-8-5 text-muted">
                  {{ restoreReason ? restoreReason.length : 0 }}/500 ký tự (tối thiểu 10)
                </span>
              </div>
            </div>
          </div>
          <div class="modal-footer-custom">
            <button @click="closeRestoreModal" class="btn-modal-cancel" :disabled="isLoading">Hủy bỏ</button>
            <button @click="restoreBook" class="btn-modal-restore" :disabled="isLoading || !restoreReason || restoreReason.trim().length < 10 || restoreReason.trim().length > 500">
              <icon v-if="isLoading" icon="fa-solid fa-spinner" class="fa-spin me-1" />
              <icon v-else icon="fa-solid fa-trash-can-arrow-up" class="me-1" />
              {{ restoreButtonText }}
            </button>
          </div>
        </div>
      </div>
    </teleport>

    <!-- ── Reason Input Modal ──────────────────────────────── -->
    <teleport to="body">
      <div v-if="reasonModalVisible" class="modal-backdrop-custom">
        <div class="modal-card-custom modal-card-sm">
          <div class="modal-sheet-handle"></div>
          <div class="modal-header-custom">
            <h3 class="modal-title-custom">
              <icon icon="fa-solid fa-circle-question" />
              Lý do chỉnh sửa
            </h3>
            <button @click="closeReasonModal" class="btn-close-modal" aria-label="Đóng">
              <icon icon="fa-solid fa-xmark" />
            </button>
          </div>

          <div class="modal-body-custom">
            <p class="text-muted-dark mb-3">Vui lòng cung cấp lý do cập nhật thông tin sách để ghi nhận lịch sử thay đổi.</p>
            <div class="form-field">
              <label class="form-field-label">Lý do thay đổi <span class="required-mark">*</span></label>
              <textarea
                v-model="form.reason"
                class="custom-textarea-modal"
                :class="{ 'input-error': errors.reason }"
                placeholder="Nhập tối thiểu 10 ký tự. Ví dụ: Cập nhật lại thông tin sách, ảnh bìa chính xác hơn..."
                ref="reasonInput"
              ></textarea>
              <div class="d-flex justify-content-between align-items-center mt-1">
                <span v-if="errors.reason" class="field-error-msg m-0">{{ errors.reason }}</span>
                <span v-else></span>
                <span class="fs-8-5 text-muted">
                  {{ form.reason ? form.reason.length : 0 }}/500 ký tự (tối thiểu 10)
                </span>
              </div>
            </div>
          </div>

          <div class="modal-footer-custom">
            <button @click="closeReasonModal" class="btn-modal-cancel" :disabled="isLoading">Hủy bỏ</button>
            <button @click="submitUpdateWithReason" class="btn-modal-confirm" :disabled="isLoading || !form.reason || form.reason.trim().length < 10 || form.reason.trim().length > 500">
              <icon v-if="isLoading" icon="fa-solid fa-spinner" class="fa-spin me-1" />
              Xác nhận
            </button>
          </div>
        </div>
      </div>
    </teleport>


    <!-- ── History Modal ──────────────────────────────────── -->
    <teleport to="body">
      <div v-if="historyModalVisible" class="modal-backdrop-custom">
        <div class="modal-card-custom modal-card-md">
          <div class="modal-header-custom">
            <h3 class="modal-title-custom">
              <icon icon="fa-solid fa-clock-rotate-left" />
              Lịch sử chỉnh sửa: {{ selectedBook?.name }}
            </h3>
            <button @click="closeHistoryModal" class="btn-close-modal" aria-label="Đóng">
              <icon icon="fa-solid fa-xmark" />
            </button>
          </div>
          <div class="modal-body-custom modal-body-history">
            <div class="history-timeline">
              <div v-for="entry in selectedBook?.history" :key="entry.id" class="timeline-item">
                <div class="timeline-badge" :class="getHistoryBadgeClass(entry.action)">
                  <icon :icon="getHistoryIcon(entry.action)" />
                </div>
                <div class="timeline-content-card">
                  <div class="timeline-header">
                    <span class="timeline-action">{{ entry.action }}</span>
                    <span class="timeline-time">{{ formatTime(entry.timestamp) }}</span>
                  </div>
                  <div class="timeline-actor">
                    <icon icon="fa-solid fa-user" />
                    <span>Thực hiện bởi: <strong>{{ entry.actor }}</strong></span>
                  </div>
                  <!-- Render dynamic details based on action -->
                  <div class="timeline-details-row">
                    <p v-if="entry.action === 'Khởi tạo'" class="timeline-details">
                      Khởi tạo đầu sách mới.
                    </p>
                    <div v-else-if="entry.action === 'Cập nhật thông tin'" class="timeline-details">
                      <div v-if="entry.fieldChanges && entry.fieldChanges.length > 0" class="field-changes-list">
                        <div v-for="change in entry.fieldChanges" :key="change.field" class="field-change-item mb-2">
                          <span class="field-label-tag"><strong>{{ translateField(change.field) }}</strong>:</span>
                          
                          <div class="field-values-diff mt-1">
                            <!-- Content field has read more logic -->
                            <template v-if="change.field === 'content'">
                              <div class="diff-value-box">
                                <span class="badge bg-secondary text-white me-1">Trước:</span>
                                <span>{{ truncateText(entry.id, change.field, 'old', change.oldValue) }}</span>
                                <button v-if="shouldShowMore(change.oldValue)" @click="toggleExpand(entry.id, change.field, 'old')" class="btn-show-more">
                                  {{ isExpanded(entry.id, change.field, 'old') ? 'Thu gọn' : 'Xem thêm' }}
                                </button>
                              </div>
                              <div class="diff-value-box mt-1">
                                <span class="badge bg-primary text-white me-1">Sau:</span>
                                <span>{{ truncateText(entry.id, change.field, 'new', change.newValue) }}</span>
                                <button v-if="shouldShowMore(change.newValue)" @click="toggleExpand(entry.id, change.field, 'new')" class="btn-show-more">
                                  {{ isExpanded(entry.id, change.field, 'new') ? 'Thu gọn' : 'Xem thêm' }}
                                </button>
                              </div>
                            </template>

                            <!-- Relations field (authors, categories, translators) -->
                            <template v-else-if="['authors', 'categories', 'translators'].includes(change.field)">
                              <div class="diff-value-box">
                                <span class="badge bg-secondary text-white me-1">Trước:</span>
                                <span>{{ formatRelationNames(change.oldValue, change.field) }}</span>
                              </div>
                              <div class="diff-value-box mt-1">
                                <span class="badge bg-primary text-white me-1">Sau:</span>
                                <span>{{ formatRelationNames(change.newValue, change.field) }}</span>
                              </div>
                            </template>

                            <!-- Image Cover field -->
                            <template v-else-if="change.field === 'cover'">
                              <div class="diff-value-box">
                                <span class="badge bg-secondary text-white me-1">Trước:</span>
                                <span>[Ảnh bìa cũ]</span>
                              </div>
                              <div class="diff-value-box mt-1">
                                <span class="badge bg-primary text-white me-1">Sau:</span>
                                <span>[Ảnh bìa mới]</span>
                              </div>
                            </template>

                            <!-- General string fields -->
                            <template v-else>
                              <div class="diff-value-box">
                                <span class="badge bg-secondary text-white me-1">Trước:</span>
                                <span class="text-dark">{{ change.oldValue || '(Trống)' }}</span>
                              </div>
                              <div class="diff-value-box mt-1">
                                <span class="badge bg-primary text-white me-1">Sau:</span>
                                <span class="text-dark">{{ change.newValue || '(Trống)' }}</span>
                              </div>
                            </template>
                          </div>
                        </div>
                      </div>
                      <span v-else>Cập nhật thông tin đầu sách.</span>
                      
                      <div v-if="entry.reason" class="mt-2 text-muted-dark" style="font-size: 0.82rem;">
                        Lý do: <strong>{{ entry.reason }}</strong>
                      </div>
                    </div>
                    <p v-else-if="entry.action === 'Cập nhật trạng thái'" class="timeline-details">
                      Yêu cầu xóa đầu sách.
                      <template v-if="entry.reason">
                        <br />
                        Lý do: <strong>{{ entry.reason }}</strong>
                      </template>
                    </p>
                    <p v-else class="timeline-details">
                      {{ entry.details }}
                    </p>
                  </div>
                </div>
              </div>
              <div v-if="!selectedBook?.history || selectedBook.history.length === 0" class="history-empty">
                <icon icon="fa-solid fa-clock" />
                <span>Chưa ghi nhận lịch sử chỉnh sửa nào cho tác phẩm này.</span>
              </div>
            </div>
          </div>
          <div class="modal-footer-custom">
            <button @click="closeHistoryModal" class="btn-modal-cancel">Đóng lại</button>
          </div>
        </div>
      </div>
    </teleport>

    <!-- ── View Modal ─────────────────────────────────────── -->
    <teleport to="body">
      <div v-if="viewModalVisible" class="modal-backdrop-custom">
        <div class="modal-card-custom modal-card-view">
          <div class="modal-header-custom">
            <h3 class="modal-title-custom">
              <icon icon="fa-solid fa-book-open" />
              Chi tiết cuốn sách
            </h3>
            <button @click="closeViewModal" class="btn-close-modal" aria-label="Đóng">
              <icon icon="fa-solid fa-xmark" />
            </button>
          </div>

          <div class="modal-body-custom">
            <div class="view-book-grid">

              <!-- Left: Cover Image -->
              <div class="view-book-left">
                <div class="cover-display-box" @click="!imageLoadError && selectedBook?.cover && openImagePreview()" :class="{ 'clickable-image': selectedBook?.cover && !imageLoadError }">
                  <img v-if="selectedBook?.cover" :src="selectedBook.cover.startsWith('data:') || selectedBook.cover.startsWith('http') ? selectedBook.cover : 'data:image/png;base64,' + selectedBook.cover" class="cover-display-img" :alt="selectedBook?.name" @error="imageLoadError = true" @load="imageLoadError = false" />
                  <div v-else class="cover-display-placeholder">
                    <icon icon="fa-solid fa-book-open" />
                  </div>
                </div>
              </div>

              <!-- Right: Book Information -->
              <div class="view-book-right">
                <!-- Book Name -->
                <div class="view-field mb-4">
                  <span class="view-field-label">Tên sách</span>
                  <p class="view-field-value">{{ selectedBook?.name }}</p>
                </div>

                <!-- Rating & Status in a clean 2-col row -->
                <div class="view-field-row mb-4">
                  <div class="view-field view-field-half">
                    <span class="view-field-label">Đánh giá</span>
                    <p class="view-field-value">
                      <icon icon="fa-solid fa-star" class="rating-star" />
                      <span>{{ selectedBook?.evaluate ? selectedBook.evaluate.toFixed(1) : '0.0' }}/5.0</span>
                    </p>
                  </div>
                  <div class="view-field view-field-half">
                    <span class="view-field-label">Trạng thái</span>
                    <p class="mb-0">
                      <span
                        class="status-badge-custom"
                        :class="getStatusBadgeClass(selectedBook)"
                        :data-tooltip="getStatusTooltip(selectedBook)"
                      >
                        {{ getStatusLabel(selectedBook) }}
                      </span>
                    </p>
                  </div>
                </div>

                <!-- Shopee Link -->
                <div v-if="selectedBook?.link_shopee" class="view-field mb-4">
                  <span class="view-field-label">Link Shopee</span>
                  <p class="view-field-value">
                    <a :href="selectedBook.link_shopee" target="_blank" rel="noopener noreferrer" class="shopee-link-full">
                      <icon icon="fa-solid fa-cart-shopping" />
                      Xem trên Shopee
                    </a>
                  </p>
                </div>
              </div>
            </div>

            <!-- Content Section -->
            <div class="view-content-section mt-4">
              <span class="view-field-label">Tóm tắt nội dung</span>
              <p class="view-content-text">{{ selectedBook?.content }}</p>
            </div>

            <!-- Relations Section -->
            <div class="view-relations-section mt-4">
              <h4 class="view-relations-title">Mối quan hệ</h4>

              <!-- Authors -->
              <div v-if="selectedBook?.authors && selectedBook.authors.length" class="view-relation-block">
                <span class="view-relation-label">
                  <icon icon="fa-solid fa-user-pen" class="me-2" />
                  Tác giả
                </span>
                <div class="view-badge-group">
                  <span
                    v-for="a in selectedBook.authors"
                    :key="a.id"
                    class="badge-author view-badge"
                    :class="{ 'badge-deleted': a.status !== 0 && a.status != null }"
                    :data-tooltip="a.status !== 0 && a.status != null ? 'Tác giả đã bị xóa' : undefined"
                  >
                    {{ a.name }}<template v-if="a.yob || a.yod"> ({{ a.yob || '?' }} - {{ a.yod || '?' }})</template>
                  </span>
                </div>
              </div>

              <!-- Translators -->
              <div v-if="selectedBook?.translators && selectedBook.translators.length" class="view-relation-block">
                <span class="view-relation-label">
                  <icon icon="fa-solid fa-language" class="me-2" />
                  Dịch giả
                </span>
                <div class="view-badge-group">
                  <span
                    v-for="t in selectedBook.translators"
                    :key="t.id"
                    class="badge-translator view-badge"
                    :class="{ 'badge-deleted': t.status !== 0 && t.status != null }"
                    :data-tooltip="t.status !== 0 && t.status != null ? 'Dịch giả đã bị xóa' : undefined"
                  >
                    {{ t.name }}
                  </span>
                </div>
              </div>

              <!-- Categories -->
              <div v-if="selectedBook?.categories && selectedBook.categories.length" class="view-relation-block">
                <span class="view-relation-label">
                  <icon icon="fa-solid fa-tags" class="me-2" />
                  Thể loại
                </span>
                <div class="view-badge-group">
                  <span
                    v-for="c in selectedBook.categories"
                    :key="c.id"
                    class="badge-category view-badge"
                    :class="{ 'badge-deleted': c.status !== 0 && c.status != null }"
                    :data-tooltip="c.status !== 0 && c.status != null ? 'Thể loại đã bị xóa' : undefined"
                  >
                    {{ c.name }}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div class="modal-footer-custom">
            <button @click="editFromView" class="btn-modal-action btn-modal-edit">
              <icon icon="fa-solid fa-pen-to-square" />
              Chỉnh sửa
            </button>
            <button @click="closeViewModal" class="btn-modal-cancel">Đóng lại</button>
          </div>
        </div>
      </div>
    </teleport>

    <!-- ── Image Preview Modal ─────────────────────────────── -->
    <teleport to="body">
      <div v-if="imagePreviewVisible" class="modal-backdrop-custom modal-backdrop-image" @click="closeImagePreview">
        <div class="modal-image-container" @click.stop>
          <button @click="closeImagePreview" class="btn-image-close" aria-label="Đóng">
            <icon icon="fa-solid fa-xmark" />
          </button>
          <img 
            v-if="selectedBook?.cover" 
            :src="selectedBook.cover.startsWith('data:') || selectedBook.cover.startsWith('http') ? selectedBook.cover : 'data:image/png;base64,' + selectedBook.cover" 
            class="image-preview-full" 
            :alt="selectedBook?.name" 
          />
        </div>
      </div>
    </teleport>

  </div>
</template>

<!-- HMR Force Trigger Comment -->
<script src="./books.js"></script>
<style scoped src="./books.css"></style>
